import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

// 1. DYNAMICALLY INJECT CSS 
if (!document.getElementById('compact-quick-add-styles')) {
  const style = document.createElement('style');
  style.id = 'compact-quick-add-styles';
  style.innerHTML = `
    /* Force Modal into Bottom/Side Drawer */
    .quick-add-modal.dialog-modal {
      position: fixed !important; bottom: 0 !important; top: auto !important; left: 0 !important; right: 0 !important;
      margin: 0 !important; width: 100vw !important; max-width: 100vw !important;
      border-radius: 16px 16px 0 0 !important; transform: translateY(100%);
      transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
      height: max-content !important; max-height: 90vh !important; background: #fff;
      overflow-y: auto; padding: 0 !important;
    }
    .quick-add-modal[open] { transform: translateY(0) !important; animation: none !important; }
    .quick-add-modal.dialog-closing { transform: translateY(100%) !important; animation: none !important; }
    
    @media (min-width: 750px) {
      .quick-add-modal.dialog-modal {
        top: 0 !important; bottom: 0 !important; right: 0 !important; left: auto !important;
        width: 420px !important; height: 100vh !important; max-height: 100vh !important;
        border-radius: 12px 0 0 12px !important; transform: translateX(100%);
      }
      .quick-add-modal[open] { transform: translateX(0) !important; animation: none !important; }
      .quick-add-modal.dialog-closing { transform: translateX(100%) !important; animation: none !important; }
    }

    /* Layout & Spacing */
    .compact-drawer-content { display: flex; flex-direction: column; padding: 24px; position: relative; }
    
    /* Header Area */
    .compact-header { display: flex; align-items: flex-start; gap: 16px; padding-bottom: 20px; border-bottom: 1px solid #e5e5e5; }
    .compact-media { width: 80px; height: 100px; flex-shrink: 0; }
    .compact-media img { width: 100%; height: 100%; object-fit: cover; border-radius: 6px; }
    .compact-info { display: flex; flex-direction: column; gap: 4px; flex-grow: 1; }
    .compact-title { font-weight: 700; font-size: 15px; color: #121212; text-decoration: none; line-height: 1.3; margin: 0; }
    .compact-price-wrapper product-price .price { font-size: 14px; color: #555; margin: 0; }
    
    /* Dynamic Size & Stock Status */
    .compact-size-status { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 13px; margin-top: auto; padding-top: 8px; }
    .compact-size-label { color: #666; font-weight: 400; }
    .compact-size-val { color: #121212; font-weight: 600; }
    .compact-stock-status { display: flex; align-items: center; gap: 4px; font-weight: 600; }
    .compact-stock-status.in-stock { color: #188038; }
    .compact-stock-status.out-of-stock { color: #d93025; }

    /* Body Area */
    .compact-body { display: block; margin-top: 20px; }
    
    /* Hide unwanted elements */
    .compact-body .cp-size-row, 
    .compact-body .cp-variant-header,
    .compact-body .cp-swatch-container { display: none !important; }

    .compact-body .cp-product-options-wrapper { display: flex; flex-direction: column; gap: 16px; margin-bottom: 16px;}
    
    /* Clean up Swatches */
    .compact-body fieldset { border: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px; }
    .compact-body .variant-option__button-label { margin: 0; padding: 0; cursor: pointer; }
    .compact-body .variant-option__button-label__text { display: flex; align-items: center; justify-content: center; min-width: 44px; height: 44px; padding: 0 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; color: #121212; transition: all 0.2s ease; background: #fff; }
    .compact-body input[type="radio"]:checked + .variant-option__button-label__text { border-color: #121212; background: #121212; color: #fff; }
    .compact-body input[type="radio"]:disabled + .variant-option__button-label__text { opacity: 0.5; cursor: not-allowed; text-decoration: line-through; }
    .compact-body .cp-custom-strikethrough { display: none !important; }

    /* Add to Cart block */
    .compact-body product-form-component { display: block; margin-top: 24px; }
    .compact-body .add-to-cart-button { width: 100%; min-height: 48px; border-radius: 4px; font-weight: bold; background: #000; color: #fff; border: none; }
    
    /* Close Button positioned neatly */
    .quick-add-modal__close { position: absolute !important; top: 16px !important; right: 16px !important; width: 32px !important; height: 32px !important; background: #f4f4f4 !important; border-radius: 50% !important; border: none !important; display: flex !important; align-items: center !important; justify-content: center !important; cursor: pointer !important; z-index: 99 !important; color: #000 !important; }
    .quick-add-modal__close:hover { background: #e5e5e5 !important; }
    .quick-add-modal__close svg { width: 12px !important; height: 12px !important; }
  `;
  document.head.appendChild(style);
}

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    if(variantPicker) variantPicker.updateVariantPicker(newHtml);
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    // 1. EXTRACT ALL ELEMENTS based on your exact theme markup
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    
    // Extract Image & Title
    // Note: Updated to look for the span class .view-product-title-text 
    const titleEl = productGrid.querySelector('.view-product-title-text, .view-product-title a');
    const productTitle = titleEl ? titleEl.textContent.trim() : (this.dataset.productTitle || '');
    
    // FAST IMAGE: Prioritize the data attribute passed directly from liquid to avoid massive images
    const imageEl = productGrid.querySelector('.product-media__image');
    const imgSrc = this.dataset.productImage || (imageEl ? imageEl.src : '');

    // 2. CLEAR MODAL TO REBUILD SAFELY
    modalContent.innerHTML = '';
    modalContent.className = 'quick-add-modal__content compact-drawer-content';

    // 3. BUILD HEADER
    const header = document.createElement('div');
    header.className = 'compact-header';
    header.innerHTML = `
      <div class="compact-media">
        ${imgSrc ? `<img src="${imgSrc}" alt="${productTitle}">` : `<div style="width:100%;height:100%;background:#eee;border-radius:4px;"></div>`}
      </div>
      <div class="compact-info">
        <a href="${this.productPageUrl}" class="compact-title">${productTitle}</a>
        <div class="compact-price-wrapper"></div>
        <div class="compact-size-status">
          <div class="compact-size-label">Size: <span class="compact-size-val">Select</span></div>
          <div class="compact-stock-status" style="display: none;"></div>
        </div>
      </div>
    `;

    // Drop dynamic price in
    if (productPrice) header.querySelector('.compact-price-wrapper').appendChild(productPrice);

    // 4. BUILD BODY
    const body = document.createElement('div');
    body.className = 'compact-body';
    
    // Create a wrapper for options to map to your CSS styling
    const optionsWrapper = document.createElement('div');
    optionsWrapper.className = 'cp-product-options-wrapper';
    
    // Add Size swatches ONLY (Color swatches explicitly excluded)
    if (variantPicker) optionsWrapper.appendChild(variantPicker);
    
    body.appendChild(optionsWrapper);
    
    // Add Add to Cart block
    if (productFormComponent) {
        body.appendChild(productFormComponent);
    }

    modalContent.appendChild(header);
    modalContent.appendChild(body);

    // 5. ATTACH DYNAMIC "IN STOCK" LOGIC
    if (variantPicker) {
      const sizeVal = header.querySelector('.compact-size-val');
      const stockStatus = header.querySelector('.compact-stock-status');

      const updateStockStatus = () => {
        const checkedInput = variantPicker.querySelector('input[type="radio"]:checked');
        
        if (checkedInput) {
          // A size is selected
          sizeVal.textContent = checkedInput.value;
          stockStatus.style.display = 'flex'; // Unhide the stock block
          
          const isAvailable = checkedInput.dataset.optionAvailable === 'true';
          
          if (isAvailable) {
            stockStatus.className = 'compact-stock-status in-stock';
            stockStatus.innerHTML = `<svg style="width:14px;height:14px;" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"></path></svg> In stock`;
          } else {
            stockStatus.className = 'compact-stock-status out-of-stock';
            stockStatus.innerHTML = `<svg style="width:14px;height:14px;" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"></path></svg> Out of stock`;
          }
        } else {
          // No size selected yet
          sizeVal.textContent = 'Select';
          stockStatus.style.display = 'none'; // Hide the stock block
          stockStatus.innerHTML = '';
        }
      };

      // Listen for when users click swatches
      variantPicker.addEventListener('change', updateStockStatus);
      
      // Keep theme's native sync working
      this.#syncVariantSelection(modalContent);
      
      // Run once on load to see if a size was pre-selected
      setTimeout(updateStockStatus, 50);
    } else {
      this.#syncVariantSelection(modalContent);
    }
    
    // 6. Execute scripts that may be embedded in the product form (like the size chart modal toggle)
    this.executeScripts(modalContent);
  }
  
  executeScripts(element) {
    const scripts = element.querySelectorAll("script");
    scripts.forEach((oldScript) => {
        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}